/*
 * OrderIdTriple — single-source-of-truth display for the 3-ID model on
 * dropship orders:
 *
 *   1. verqo code  (DS-XXXXX)            — primary, our platform's ID
 *   2. Buyer's Shopify ID  (#1042)       — the buyer's storefront order #
 *   3. Seller's Shopify ID  (#3501)      — the seller's fulfillment shop's order #
 *
 * Designed to live INSIDE the existing 110px-wide order-code column on
 * both buyer + seller daily-order rows. Lines 2/3 are tiny secondary IDs
 * that stack vertically; clicking either one opens the corresponding
 * Shopify admin page in a new tab. When `compact=true` the lines collapse
 * into chips on a single row (used in claim/return drawers).
 */
import { ExternalLink, Upload, FileText } from "lucide-react";

export default function OrderIdTriple({ order, compact = false, role }) {
  const be = order?.buyer_external;
  const se = order?.seller_external;

  const buyerLabel = be?.order_number || (be?.order_id ? `#${be.order_id.slice(0, 6)}` : null);
  const sellerLabel = se?.order_number || (se?.order_id ? `#${se.order_id.slice(0, 6)}` : null);

  const showBuyerLine = !!be?.order_id;
  const showSellerLine = !!se?.order_id;

  // The buyer's storefront admin URL is only openable by the buyer. For
  // sellers viewing wholesale dropship orders, suppress the external
  // link (seller has no access to the buyer's Shopify admin). We keep
  // the buyer's order # visible for reference, just non-clickable.
  // Seller-owned channel orders (Amazon/TikTok/Walmart) surface their
  // marketplace URL separately via `external_marketplace_url` on the
  // drawer — this rule doesn't affect them.
  const buyerUrlForRole = role === "seller" ? null : be?.admin_url;

  // Source badge — keep parity with the existing CSV pill.
  const SourceBadge = order?.source === "csv_upload" ? (
    <span title={role === "seller" ? "Uploaded via CSV by buyer" : "Uploaded via CSV"}
          className="text-[9.5px] mono uppercase tracking-[0.14em] px-1 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 inline-flex items-center gap-0.5"
          data-testid={`order-source-csv-${order?.code}`}>
      {role === "seller" ? <FileText className="w-2.5 h-2.5"/> : <Upload className="w-2.5 h-2.5"/>}
      CSV
    </span>
  ) : order?.source === "shopify_webhook" ? (
    <span title="Auto-imported from Shopify"
          className="text-[9.5px] mono uppercase tracking-[0.14em] px-1 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
          data-testid={`order-source-shopify-${order?.code}`}>
      SHOPIFY
    </span>
  ) : order?.source_type === "channel" && order?.source_channel_type ? (
    /* Phase-2 · Category A2 · Sales-channel source badge.
       Orders sourced from a seller-owned marketplace (Amazon / TikTok /
       Walmart / …) get a tinted pill in the same slot as other source
       badges. Colour follows the setup-tab channel accents. */
    (() => {
      const t = order.source_channel_type;
      const cls =
        t === "amazon"      ? "bg-amber-50 text-amber-800 border-amber-200"   :
        t === "tiktok"      ? "bg-rose-50 text-rose-800 border-rose-200"      :
        t === "walmart"     ? "bg-sky-50 text-sky-800 border-sky-200"         :
        t === "shopify_dtc" ? "bg-emerald-50 text-emerald-800 border-emerald-200":
        t === "etsy"        ? "bg-orange-50 text-orange-800 border-orange-200":
        t === "ebay"        ? "bg-indigo-50 text-indigo-800 border-indigo-200":
                              "bg-secondary text-foreground/70 border-border";
      const label = { amazon:"AMAZON", tiktok:"TIKTOK", walmart:"WALMART",
                      shopify_dtc:"SHOPIFY", etsy:"ETSY", ebay:"EBAY" }[t] || t.toUpperCase();
      return (
        <span
          title={`Direct sale · ${order.source_channel_label || label}${order.external_order_id ? " · " + order.external_order_id : ""}`}
          className={`text-[9.5px] mono uppercase tracking-[0.14em] px-1 py-0.5 rounded-full border ${cls}`}
          data-testid={`order-source-channel-${order?.code}`}
        >{label}</span>
      );
    })()
  ) : null;

  if (compact) {
    return (
      <div className="inline-flex items-center gap-1.5 flex-wrap" data-testid={`order-id-triple-${order?.code}`}>
        <span className="mono text-[12px] font-medium">{order?.code}</span>
        {SourceBadge}
        {showBuyerLine && (
          <ExtIdPill label="buyer" id={buyerLabel} url={buyerUrlForRole}
                     testid={`buyer-ext-${order?.code}`}/>
        )}
        {showSellerLine && (
          <ExtIdPill label="seller" id={sellerLabel} url={se.admin_url}
                     testid={`seller-ext-${order?.code}`}/>
        )}
      </div>
    );
  }

  return (
    <div className="mono text-[12px] flex flex-col gap-0.5 min-w-0" data-testid={`order-id-triple-${order?.code}`}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="font-medium">{order?.code}</span>
        {SourceBadge}
      </div>
      {(showBuyerLine || showSellerLine) && (
        <div className="flex flex-col gap-0.5">
          {showBuyerLine && (
            <ExtIdLine label="buyer" id={buyerLabel} url={buyerUrlForRole}
                       testid={`buyer-ext-${order?.code}`}/>
          )}
          {showSellerLine && (
            <ExtIdLine label="seller" id={sellerLabel} url={se.admin_url}
                       testid={`seller-ext-${order?.code}`}/>
          )}
        </div>
      )}
    </div>
  );
}

function ExtIdPill({ label, id, url, testid }) {
  const inner = (
    <span className="inline-flex items-center gap-0.5 text-[9.5px] mono uppercase tracking-[0.12em] px-1 py-0.5 rounded-full bg-foreground/5 text-foreground/70 border border-foreground/10 hover:bg-foreground/10">
      <span className="text-foreground/45">{label}</span>
      <span className="font-medium">{id}</span>
      {url && <ExternalLink className="w-2.5 h-2.5 opacity-60"/>}
    </span>
  );
  if (url) return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={testid} title={`Open in Shopify admin: ${id}`}>{inner}</a>
  );
  return <span data-testid={testid} title={`${label}: ${id}`}>{inner}</span>;
}

function ExtIdLine({ label, id, url, testid }) {
  const text = (
    <span className="inline-flex items-center gap-1 text-[10px] text-foreground/55 hover:text-foreground transition-colors">
      <span className="uppercase tracking-[0.12em] text-foreground/40">{label}</span>
      <span className="font-medium">{id}</span>
      {url && <ExternalLink className="w-2 h-2 opacity-50"/>}
    </span>
  );
  if (url) return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={testid}
       title={`Open in Shopify admin: ${id}`}>{text}</a>
  );
  return <span data-testid={testid} title={`${label}: ${id}`}>{text}</span>;
}
